import { useEffect } from 'react';
import { Input, Label } from '@kmgestao/ui';

import { normalizeCnpj, useCnpjLookup } from '../../../leads/cnpj-lookup.js';
import { maskCnpjProgressive, toTitleCase } from '../../../leads/formatters.js';
import type { FormField } from '../../schemas.js';

export interface CnpjFieldProps {
  field: FormField;
  value: string;
  onChange: (next: string) => void;
  /** Callback disparado quando o BrasilAPI retorna dados — passa o
   * objeto inteiro para o renderer pré-popular outros campos (razão
   * social, endereço, etc.) quando `field.autofill` está ativo. */
  onAutofill?: (data: {
    legal_name?: string | undefined;
    trade_name?: string | undefined;
    phone?: string | undefined;
    email?: string | undefined;
    address_cep?: string | undefined;
    address_street?: string | undefined;
    address_number?: string | undefined;
    address_complement?: string | undefined;
    address_district?: string | undefined;
    address_city?: string | undefined;
    address_uf?: string | undefined;
  }) => void;
  onBlur?: (() => void) | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
}

/**
 * Campo de CNPJ com máscara progressiva e autofill opcional via
 * BrasilAPI (mesma integração usada no `LeadFormFields` — reusa
 * `useCnpjLookup`).
 *
 * Quando `field.autofill === 'brasilapi-cnpj'` e 14 dígitos são
 * digitados, consulta a API e chama `onAutofill` com um objeto
 * normalizado. O renderer do form fica responsável por decidir quais
 * campos receber o autofill (via `id` — ex: `razao_social`,
 * `endereco_rua`).
 *
 * Mostra feedback inline de loading/erro abaixo do input,
 * consistente com o padrão do `LeadFormFields`.
 */
export function CnpjField({
  field,
  value,
  onChange,
  onAutofill,
  onBlur,
  error,
  disabled,
  idPrefix = 'form',
}: CnpjFieldProps) {
  const inputId = `${idPrefix}-${field.id}`;
  const descId = field.description ? `${inputId}-desc` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;

  const digits = normalizeCnpj(value);
  const autofillEnabled = field.autofill === 'brasilapi-cnpj';
  const cnpjQuery = useCnpjLookup(autofillEnabled ? digits : '');

  // Dispara `onAutofill` quando BrasilAPI retorna. Só toca campos
  // vazios — a página pública não deve sobrescrever dados que o lead
  // já confirmou. Controle fica com o pai via estratégia
  // "respect existing values" (equivalente ao NewLeadDialog).
  useEffect(() => {
    if (!cnpjQuery.data || !onAutofill) return;
    const d = cnpjQuery.data;
    onAutofill({
      legal_name: d.razao_social ? toTitleCase(d.razao_social) : undefined,
      trade_name: d.nome_fantasia ? toTitleCase(d.nome_fantasia) : undefined,
      phone: d.ddd_telefone_1 ?? undefined,
      email: d.email?.trim().toLowerCase(),
      address_cep: d.cep ?? undefined,
      address_street: d.logradouro ? toTitleCase(d.logradouro) : undefined,
      address_number: d.numero?.trim(),
      address_complement: d.complemento ? toTitleCase(d.complemento) : undefined,
      address_district: d.bairro ? toTitleCase(d.bairro) : undefined,
      address_city: d.municipio ? toTitleCase(d.municipio) : undefined,
      address_uf: d.uf?.toUpperCase().slice(0, 2),
    });
  }, [cnpjQuery.data, onAutofill]);

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>
        {field.label}
        {field.required ? <span className="ml-0.5 text-destructive"> *</span> : null}
      </Label>
      {field.description ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {field.description}
        </p>
      ) : null}
      <Input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => {
          const raw = normalizeCnpj(e.target.value);
          onChange(maskCnpjProgressive(raw));
        }}
        onBlur={onBlur}
        placeholder={field.placeholder ?? '00.000.000/0000-00'}
        autoComplete="off"
        inputMode="numeric"
        maxLength={18}
        hasError={!!error}
        disabled={disabled}
        aria-describedby={[descId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={!!error}
      />
      {autofillEnabled && cnpjQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">Consultando Receita Federal…</p>
      ) : null}
      {autofillEnabled && cnpjQuery.isError ? (
        <p className="text-xs text-destructive" role="alert">
          {(cnpjQuery.error as Error).message}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
